import { isTaseronRole } from "./lib/roles";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthCallback from "@/components/AuthCallback";
import { Toaster } from "sonner";
import { RecordFlashAgent } from "@/components/RecordFlashAgent";
import IdleGuard from "@/components/IdleGuard";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute, { AppSplash } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DataEntry from "@/pages/DataEntry";
import VeriGirisi from "@/pages/VeriGirisi";
import CustomerClaim from "@/pages/CustomerClaim";
import SubcontractorClaim from "@/pages/SubcontractorClaim";
import ProjectCost from "@/pages/ProjectCost";
import Invoices from "@/pages/Invoices";
import Demirbaslar from "@/pages/Demirbaslar";
import DemirbasKart from "@/pages/DemirbasKart";
import Stoklar from "@/pages/Stoklar";
import MaliVeriler from "@/pages/MaliVeriler";
import SabitTanimlar from "@/pages/def/SabitTanimlar";
import Holidays from "@/pages/def/Holidays";
import LeaveCalendar from "@/pages/LeaveCalendar";
import PaymentPlan from "@/pages/PaymentPlan";
import Budget from "@/pages/Budget";
import IssuedInvoices from "@/pages/IssuedInvoices";
import Checks from "@/pages/Checks";
import Projects from "@/pages/def/Projects";
import Services from "@/pages/def/Services";
import Users from "@/pages/def/Users";
import RoleMatrix from "@/pages/def/RoleMatrix";
import Backup from "@/pages/def/Backup";
import BudgetItems from "@/pages/def/BudgetItems";
import Caris from "@/pages/Caris";
import Approvals from "@/pages/Approvals";
import ChangePassword from "@/pages/ChangePassword";
import WorkOrders from "@/pages/WorkOrders";
import AtmWipes from "@/pages/AtmWipes";
import Subcontractors from "@/pages/Subcontractors";
import SatisPazarlama from "@/pages/SatisPazarlama";
import Muhasebe from "@/pages/Muhasebe";
import Bordro from "@/pages/Bordro";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteMemory, homeRoute } from "@/lib/routeMemory";

// "/" adresine düşüldüğünde (sayfa yenileme vb.) son ziyaret edilen ekrana dönülür; yoksa Genel Bakış
function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <AppSplash />;   // oturum doğrulanana kadar login formu/beyaz ekran yanıp sönmesin
  return <Navigate to={homeRoute(user)} replace />;
}

// Taşeron yalnız İş Emirleri ekranını görür; diğer sayfalar oraya yönlenir
function RoleGate({ children }) {
  const { user } = useAuth();
  if (isTaseronRole(user?.role)) return <Navigate to="/is-emirleri" replace />;
  return children;
}


function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}
function AdminShell({ children }) {
  return (
    <ProtectedRoute adminOnly>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

// Google girişinden dönüş (#session_id=…) render sırasında, ProtectedRoute çalışmadan yakalanır (useLocation().hash — window.location.hash reaktif değil)
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={<Shell><RoleGate><Dashboard /></RoleGate></Shell>} />
            <Route path="/onaylar" element={<Shell><Approvals /></Shell>} />
            <Route path="/is-emirleri" element={<Shell><WorkOrders /></Shell>} />
            <Route path="/atm-silim" element={<Shell><AtmWipes /></Shell>} />
            <Route path="/taseronlar" element={<AdminShell><Subcontractors /></AdminShell>} />
            <Route path="/sifre-degistir" element={<Shell><ChangePassword /></Shell>} />
            <Route path="/veri-girisi" element={<Shell><VeriGirisi /></Shell>} />
            <Route path="/izin-takvimi" element={<Shell><LeaveCalendar /></Shell>} />
            <Route path="/musteri-hakedis" element={<Shell><CustomerClaim /></Shell>} />
            <Route path="/taseron-hakedis" element={<Shell><SubcontractorClaim /></Shell>} />
            <Route path="/proje-maliyet" element={<Shell><ProjectCost /></Shell>} />
            <Route path="/satis-pazarlama" element={<Shell><SatisPazarlama /></Shell>} />
            <Route path="/muhasebe" element={<Shell><Muhasebe /></Shell>} />
            <Route path="/bordro" element={<Shell><Bordro /></Shell>} />
            <Route path="/faturalar" element={<Navigate to="/veri-girisi?bolum=fatura" replace />} />
            <Route path="/odeme-plani" element={<Shell><PaymentPlan /></Shell>} />
            <Route path="/demirbaslar" element={<Shell><Demirbaslar /></Shell>} />
            <Route path="/demirbas/:id" element={<Shell><DemirbasKart /></Shell>} />
            <Route path="/stoklar" element={<Shell><Stoklar /></Shell>} />
            <Route path="/mali-veriler" element={<Shell><MaliVeriler /></Shell>} />
            <Route path="/butce" element={<Shell><Budget /></Shell>} />
            <Route path="/kesilen-faturalar" element={<Shell><IssuedInvoices /></Shell>} />
            <Route path="/cekler" element={<Navigate to="/veri-girisi?bolum=cek" replace />} />
            <Route path="/tanimlar/projeler" element={<AdminShell><Projects /></AdminShell>} />
            <Route path="/tanimlar/hizmetler" element={<AdminShell><Services /></AdminShell>} />
            <Route path="/tanimlar/butce-kalemleri" element={<AdminShell><BudgetItems /></AdminShell>} />
            <Route path="/tanimlar/sabit-tanimlar" element={<AdminShell><SabitTanimlar /></AdminShell>} />
            <Route path="/tanimlar/tatiller" element={<Shell><Holidays standalone /></Shell>} />
            <Route path="/tanimlar/kullanicilar" element={<AdminShell><Users /></AdminShell>} />
            <Route path="/tanimlar/yetki-matrisi" element={<AdminShell><RoleMatrix /></AdminShell>} />
            <Route path="/tanimlar/yedek" element={<AdminShell><Backup /></AdminShell>} />
            <Route path="/cariler" element={<Shell><Caris /></Shell>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App" translate="no">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <RouteMemory />
          <RecordFlashAgent />
          <IdleGuard />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
